import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";

// Default sections
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isEmptyLastTierCommission } from "@/PayPlans/components/Defaults/Managers/Finance/DefaultCommissions/utils";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// Tier components
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// MUI
import { Collapse, Grid, Stack, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Constants
const MAX_INPUT_VALUE = 1000000;

const isCommissionValid = ({ isSomeConfigured, tiers = [] }) => isSomeConfigured || !isEmptyLastTierCommission({ tiers });
export default function PVRvsPP(props) {
	const {
		title = "",
		tiers = [],
		backendProductsCount = 0,
		isSomeConfigured = true,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	// Errors
	const headerErrors = errors?.get("penetration");
	const rowsErrors = errors?.get("pvr");
	const columnsErrors = errors?.get("commissions");

	// Control to send update actions
	const wasUpdated = React.useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		const isAllValid = isValid && isCommissionValid({ isSomeConfigured, tiers: updatedTiers });
		onActionValidation?.(isAllValid);
		if (isAllValid && wasUpdated.current) {
			onActionUpdate?.(updatedTiers);
		}
	};

	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Control Last Commission Error
	const lastCommissionError = !isCommissionValid({ isSomeConfigured, tiers: innerTiers });

	// Methods
	const handleAddColumn = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = updatedTiers?.[0]?.["commissions"]?.length ? updatedTiers[0]["commissions"].length - 1 : 0;
		updatedTiers.forEach((tier) => {
			tier.commissions.push(
				ComplexCommission([0, "Unit"], [MAX_INPUT_VALUE, "Unit"], { identifier: "commission", commission: [0, "Percent"] })
			);

			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = false;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers?.[0]?.["commissions"]?.length > 1) {
			updatedTiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = tier?.commissions?.length ? tier.commissions.length - 1 : 0;
				tier.commissions[lastIndex]["to"]["value"] = MAX_INPUT_VALUE;
			});
		}
		update(updatedTiers);
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;

		const previousCommissionsLength = updatedTiers?.[lastIndex]?.["commissions"]?.length;

		const commissions = [];
		const previousFrom = updatedTiers?.[0]?.["commissions"]?.[0]?.["from"]?.["value"] || 0;
		const previousTo = updatedTiers?.[0]?.["commissions"]?.[0]?.["to"]?.["value"] || 0;
		for (let i = 0; i < previousCommissionsLength; i++) {
			commissions[i] = ComplexCommission([previousFrom, "Unit"], [previousTo, "Unit"], {
				identifier: "commission",
				commission: [0, "Percent"],
			});
		}

		updatedTiers.push(ComplexCommissionTier([0, "Dollar"], [MAX_INPUT_VALUE, "Dollar"], { commissions }));

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = false;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		updatedTiers[lastIndex].to.value = MAX_INPUT_VALUE;
		update(updatedTiers);
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission, key) => {
						if (area in commission && key === column) {
							commission[area]["value"] = updatedValue;
							if (tier.commissions?.[key + 1]) {
								tier.commissions[key + 1]["from"]["value"] = updatedValue + 0.01;
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "commission") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = updatedValue + 0.01;
					}
				} else {
					updatedTiers[row]["commissions"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update(updatedTiers);
	};

	const update = (updatedTiers) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "pvr",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "penetration",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Unit: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Product Penetration invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper <= lower is good,
									deltas: { Percent: 0 },
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers || [],
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));

		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	return (
		<Stack width={1} my={2}>
			{Boolean(title) && (
				<Typography className="!font-montserrat" variant={"h6"} color="var(--text-color)" paddingTop={"3px"}>
					{title}
				</Typography>
			)}

			<Grid container item xs={12} mt={2.8}>
				<Grid item xs={12}>
					<TierColumns
						titleLabel={"Prod. Pen."}
						titleContainerProps={{
							justifyContent: "flex-start",
							alignItems: "end",
							sx: { maxWidth: "235px", width: "235px" },
						}}
						titleProps={{
							className: "tiered-title tiered-title-adjustment",
							sx: null,
						}}
						disabledDelete={innerTiers?.[0]?.commissions.length <= 1 || readOnly}
						disabledAdd={innerTiers?.[0]?.commissions.length >= 7 || readOnly}
						onClickDelete={() => handleDeleteColumn()}
						onClickAdd={() => handleAddColumn()}
					>
						<Stack direction={"row"} position={"relative"}>
							{innerTiers?.[0]?.commissions?.map((tier, key) => {
								const fromDisabled = Boolean(!key);
								const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);
								const itemKey = `pvr_column_${key}`;
								return (
									<TierColumnsItem key={itemKey} index={key} title={"Comm."}>
										<MatrixColumn
											index={key}
											item={tier}
											errors={headerErrors}
											fromDisabled={fromDisabled}
											toDisabled={toDisabled}
											readOnly={readOnly}
											onActionUpdate={handleUpdateValue}
											backendProductsCount={backendProductsCount}
										/>
									</TierColumnsItem>
								);
							})}
						</Stack>
					</TierColumns>
				</Grid>

				<Grid item xs={12} mt={1} minHeight={"100px"}>
					<TierRows
						showTitle
						title={"PVR"}
						sxTitlePropsContainer={{
							top: "84px",
							left: "30px",
						}}
						centerTitleAfterRow={1}
						deleteButtonText={"Remove Tier"}
						addButtonText={"Add Tier"}
						deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
						addButtonDisabled={innerTiers?.length >= 50 || readOnly}
						spaces={{
							mainSection: 226,
							columns: 101,
							totalColumns: innerTiers?.[0]?.commissions?.length || 1,
							totalRows: innerTiers?.length || 1,
							separation: 5.5,
						}}
						rowsAreaProps={{
							ml: "35px",
						}}
						onClickDelete={() => handleDeleteTier()}
						onClickAdd={() => handleAddTier()}
					>
						<TransitionGroup>
							<Collapse>
								<Stack direction="column" rowGap={2}>
									{innerTiers?.map((tier, key) => {
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
										const reduction = commissionsLength * 1.55 + 2.5 + commissionsLength;
										const itemKey = `pvr_row_${key}`;
										return (
											<Grid key={itemKey} container item>
												<MatrixRow
													index={key}
													item={tier}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													spaces={{
														mainSection: 226,
														columns: 101,
														totalColumns: innerTiers?.[0]?.commissions?.length || 1,
														totalRows: innerTiers?.tiers?.length || 1,
														separation: 5.5,
														reduction,
													}}
													rowsErrors={rowsErrors}
													columnsErrors={columnsErrors}
													lastCommissionError={lastCommissionError}
													readOnly={readOnly}
													spacing={"9px"}
													sxMainContent={{ minWidth: "226.5px" }}
													sxCells={{ minWidth: "100.42px" }}
													onActionUpdate={handleUpdateValue}
												/>
											</Grid>
										);
									})}
								</Stack>
							</Collapse>
						</TransitionGroup>

						{Boolean(localErrorsMessages?.length || lastCommissionError) && (
							<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
								{localErrorsMessages.map((message, key) => (
									<TierAlertError key={`error_pvr_vs_pp_${message}_${key}`} text={message} />
								))}
								{lastCommissionError && <TierAlertError text={"Last commission must be configured"} />}
							</Grid>
						)}
					</TierRows>
				</Grid>
			</Grid>
		</Stack>
	);
}
PVRvsPP.propTypes = {
	title: PropTypes.string,
	tiers: PropTypes.array,
	backendProductsCount: PropTypes.number,
	isSomeConfigured: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
